import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plug, CheckCircle2, Send, ShieldAlert, Loader2, Mail, Copy, RefreshCw, AlertCircle, Webhook, ExternalLink } from "lucide-react";
import ResourceCategoryCleanupCard from "@/components/ResourceCategoryCleanupCard";

export default function Integrations() {
  const [data, setData] = useState(null);
  const [email, setEmail] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  const [emailResult, setEmailResult] = useState(null);
  const [emailTo, setEmailTo] = useState("");
  const [logs, setLogs] = useState(null);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsFilter, setLogsFilter] = useState("all"); // all | failed | timesheet
  const [uniteUs, setUniteUs] = useState(null);
  const [uniteUsRows, setUniteUsRows] = useState(null);
  const [uniteUsLoading, setUniteUsLoading] = useState(false);

  const load = async () => {
    try {
      const [teams, em] = await Promise.all([
        api.get("/integrations/teams"),
        api.get("/integrations/email"),
      ]);
      setData(teams.data);
      setEmail(em.data);
      setEmailTo(em.data?.recipient_default || "");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const loadUniteUs = async () => {
    setUniteUsLoading(true);
    try {
      const [status, recent] = await Promise.all([
        api.get("/integrations/unite-us/status"),
        api.get("/integrations/unite-us/recent", { params: { limit: 10 } }),
      ]);
      setUniteUs(status.data);
      setUniteUsRows(recent.data?.rows || []);
    } catch (e) {
      // Non-admins can't read these — soft-fail.
      setUniteUs({ unavailable: true });
      setUniteUsRows([]);
    } finally {
      setUniteUsLoading(false);
    }
  };

  const loadLogs = async () => {
    setLogsLoading(true);
    try {
      const params = { limit: 50 };
      if (logsFilter === "failed") params.only_failed = true;
      const qs = new URLSearchParams(params).toString();
      const { data } = await api.get(`/integrations/email/logs?${qs}`);
      let rows = data?.rows || [];
      if (logsFilter === "timesheet") {
        rows = rows.filter((r) => (r.source || "").startsWith("timesheet"));
      }
      setLogs(rows);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLogsLoading(false);
    }
  };

  useEffect(() => { load(); loadUniteUs(); }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadLogs(); }, [logsFilter]);

  const toggle = async (key, locked) => {
    if (locked) return;
    const next = { ...data.events, [key]: !data.events[key] };
    setData({ ...data, events: next });
    setSaving(true);
    try {
      await api.patch("/integrations/teams", { events: next });
    } catch (e) {
      toast.error(formatApiError(e));
      load();
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/integrations/teams/test");
      if (data?.ok) toast.success("Test ping accepted by Teams — check your channel.");
      else if (data?.skipped) toast.error(data?.reason || "Webhook not configured");
      else toast.error(`Webhook responded with status ${data?.status || "?"}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setTesting(false);
    }
  };

  const sendTestEmail = async () => {
    setTestingEmail(true);
    setEmailResult(null);
    try {
      const { data } = await api.post("/integrations/email/test", { to: emailTo });
      setEmailResult(data);
      if (data?.ok) {
        toast.success(`Resend accepted the email — id ${data.id?.slice(0, 8)}…`);
      } else {
        toast.error(data?.error || "Resend rejected the request — see details below.");
      }
      loadLogs();
    } catch (e) {
      toast.error(formatApiError(e));
      setEmailResult({ ok: false, error: formatApiError(e) });
    } finally {
      setTestingEmail(false);
    }
  };

  const copyId = () => {
    if (!emailResult?.id) return;
    navigator.clipboard?.writeText(emailResult.id);
    toast.success("Message id copied to clipboard.");
  };

  if (!data || !email) return <div className="text-center py-12 text-[#1E3A8A]">Loading…</div>;

  return (
    <div className="space-y-6" data-testid="integrations-page">
      <div>
        <div className="hw-eyebrow mb-2">Admin</div>
        <h1 className="font-display text-3xl md:text-4xl font-semibold text-[#0F172A]">Integrations</h1>
        <p className="text-sm text-[#334155] mt-1 max-w-2xl">
          Manage third-party services HarborWell Connect talks to.
        </p>
      </div>

      {/* Resend email card */}
      <div className="bg-white border border-[#E5E5E0] rounded-2xl p-6" data-testid="resend-card">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#FEE4E2] text-[#9F1239] flex items-center justify-center">
              <Mail className="w-5 h-5" strokeWidth={1.75} />
            </div>
            <div>
              <h2 className="font-display text-lg font-semibold text-[#0F172A]">Resend (transactional email)</h2>
              <p className="text-xs text-[#1E3A8A] mt-0.5">
                Sends timesheet, ticket, appointment, and digest emails. Use the test button to verify production deliverability.
              </p>
              <div className="mt-2 flex items-center gap-2 flex-wrap">
                {email.configured ? (
                  <span
                    data-testid="resend-configured-badge"
                    className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#E8ECE7] text-[#166534] font-semibold"
                  >
                    <CheckCircle2 className="w-3 h-3" strokeWidth={2} /> API key configured
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#FEF3C7] text-[#92400E] font-semibold">
                    <ShieldAlert className="w-3 h-3" strokeWidth={2} /> Not configured
                  </span>
                )}
                {email.sender_domain && (
                  <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#F1F5F9] text-[#1E3A8A] font-semibold">
                    Sender domain: {email.sender_domain}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-[#F0EFEB] pt-4 grid sm:grid-cols-2 gap-4 text-xs text-[#334155]">
          <div>
            <div className="hw-eyebrow text-[10px] mb-1">From</div>
            <div className="font-mono text-[12px] break-all">{email.email_from_name ? `${email.email_from_name} <${email.email_from}>` : (email.email_from || "—")}</div>
          </div>
          <div>
            <div className="hw-eyebrow text-[10px] mb-1">Reply-To</div>
            <div className="font-mono text-[12px] break-all">{email.email_reply_to_name ? `${email.email_reply_to_name} <${email.email_reply_to}>` : (email.email_reply_to || "—")}</div>
          </div>
          <div>
            <div className="hw-eyebrow text-[10px] mb-1">Appointment sender</div>
            <div className="font-mono text-[12px] break-all">{email.appointment_email_from || "—"}</div>
          </div>
          <div>
            <div className="hw-eyebrow text-[10px] mb-1">API key</div>
            <div className="font-mono text-[12px]">{email.api_key_masked || "—"}</div>
          </div>
          <div className="sm:col-span-2">
            <div className="hw-eyebrow text-[10px] mb-1">Resend domain verification</div>
            {email.domain_status === "verified" ? (
              <div className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-full bg-[#ECFDF5] text-[#065F46] border border-[#A7F3D0]">
                <CheckCircle2 className="w-3 h-3" /> {email.sender_domain} is verified (DKIM/SPF OK)
              </div>
            ) : email.domain_status ? (
              <div className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-full bg-[#FEF3C7] text-[#92400E] border border-[#FCD34D]">
                <ShieldAlert className="w-3 h-3" /> Status: {email.domain_status}
                {email.domain_status !== "verified" && (
                  <span className="ml-1">— add the DKIM/SPF records in <a href="https://resend.com/domains" target="_blank" rel="noopener noreferrer" className="underline">resend.com/domains</a>.</span>
                )}
              </div>
            ) : email.domains_error ? (
              <div className="text-[11px] text-[#7E937D]">Could not query Resend Domains API: {email.domains_error}</div>
            ) : (
              <div className="text-[11px] text-[#7E937D]">Not checked.</div>
            )}
          </div>
        </div>

        <div className="border-t border-[#F0EFEB] pt-4 mt-4">
          <div className="hw-eyebrow text-[10px] mb-2">Send a diagnostic email</div>
          <div className="flex gap-2 flex-wrap items-center">
            <input
              type="email"
              value={emailTo}
              onChange={(e) => setEmailTo(e.target.value)}
              placeholder="you@example.com"
              data-testid="resend-test-to"
              className="flex-1 min-w-[220px] px-3 py-2 text-sm border border-[#E5E5E0] rounded-full bg-white focus:outline-none focus:ring-2 focus:ring-[#1E3A8A]/30"
            />
            <button
              onClick={sendTestEmail}
              disabled={testingEmail || !email.configured || !emailTo}
              data-testid="resend-test-btn"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#1E3A8A] text-white text-sm font-medium disabled:opacity-60 hover:bg-[#172554]"
            >
              {testingEmail ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Send test email
            </button>
          </div>

          {emailResult && (
            <div
              data-testid="resend-test-result"
              className={`mt-3 p-3 rounded-xl border text-xs ${emailResult.ok ? "bg-[#ECFDF5] border-[#A7F3D0] text-[#065F46]" : "bg-[#FEF2F2] border-[#FECACA] text-[#991B1B]"}`}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="font-semibold">
                  {emailResult.ok ? "Resend accepted the request" : "Resend rejected the request"}
                </div>
                {emailResult.id && (
                  <button
                    onClick={copyId}
                    className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-full bg-white/70 border border-current/20"
                  >
                    <Copy className="w-3 h-3" /> Copy id
                  </button>
                )}
              </div>
              {emailResult.id && (
                <div className="font-mono break-all mt-1">id: {emailResult.id}</div>
              )}
              {emailResult.error && (
                <div className="font-mono break-words mt-1">error: {emailResult.error}</div>
              )}
              <div className="mt-2 text-[#334155]">
                {emailResult.ok ? (
                  <>Look for the email in your inbox <strong>and your Junk/Quarantine folder</strong>. To confirm final delivery, log into <a href="https://resend.com/emails" target="_blank" rel="noopener noreferrer" className="underline">resend.com → Emails</a> and search the id above — the status will be Delivered, Bounced, or Dropped.</>
                ) : (
                  <>If the error mentions &ldquo;domain not verified&rdquo; or &ldquo;from address,&rdquo; add a DKIM/SPF record for <strong>{email.sender_domain}</strong> in the Resend dashboard.</>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Recent email send attempts — server-side audit log */}
      <div className="bg-white border border-[#E5E5E0] rounded-2xl p-6" data-testid="email-logs-card">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
          <div>
            <h2 className="font-display text-lg font-semibold text-[#0F172A]">Recent email attempts</h2>
            <p className="text-xs text-[#1E3A8A] mt-0.5">
              Every send the backend attempts is logged here — including timesheet, ticket, appointment, and diagnostic emails.
              An empty row means the function was never called; a row with a Resend id means the provider accepted the request.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="inline-flex rounded-full bg-[#F9F8F6] border border-[#E5E5E0] p-1 text-xs">
              {["all", "timesheet", "failed"].map((f) => (
                <button
                  key={f}
                  onClick={() => setLogsFilter(f)}
                  data-testid={`logs-filter-${f}`}
                  className={`px-3 py-1 rounded-full ${logsFilter === f ? "bg-[#1E3A8A] text-white" : "text-[#1E3A8A]"}`}
                >
                  {f === "all" ? "All" : f === "timesheet" ? "Timesheet only" : "Failed only"}
                </button>
              ))}
            </div>
            <button
              onClick={loadLogs}
              data-testid="logs-refresh-btn"
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-white border border-[#E5E5E0] text-xs text-[#1E3A8A] hover:bg-[#F9F8F6]"
            >
              {logsLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} Refresh
            </button>
          </div>
        </div>

        {logs === null ? (
          <div className="text-xs text-[#7E937D]">Loading…</div>
        ) : logs.length === 0 ? (
          <div className="p-4 rounded-xl bg-[#FEF3C7] border border-[#FCD34D] text-xs text-[#92400E] inline-flex items-start gap-2">
            <AlertCircle className="w-4 h-4 flex-none mt-0.5" />
            <div>
              <div className="font-semibold">No email attempts logged yet.</div>
              <div className="mt-1">If the backend never tried to send an email, the trigger code may not be running. Try submitting a timesheet then refresh this view — a row should appear within seconds.</div>
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto border border-[#E5E5E0] rounded-xl" data-testid="email-logs-table">
            <table className="w-full text-xs">
              <thead className="bg-[#F9F8F6] text-[#1E3A8A] text-left">
                <tr>
                  <th className="px-3 py-2 font-semibold">When</th>
                  <th className="px-3 py-2 font-semibold">Source</th>
                  <th className="px-3 py-2 font-semibold">To</th>
                  <th className="px-3 py-2 font-semibold">Subject</th>
                  <th className="px-3 py-2 font-semibold">Status</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((r, i) => (
                  <tr key={`${r.ts}-${i}`} className="border-t border-[#F0EFEB] align-top">
                    <td className="px-3 py-2 whitespace-nowrap text-[#334155]">{r.ts ? new Date(r.ts).toLocaleString() : "—"}</td>
                    <td className="px-3 py-2 font-mono text-[11px] text-[#1E3A8A]">{r.source || "unknown"}</td>
                    <td className="px-3 py-2 font-mono text-[11px] text-[#0F172A] break-all">{r.to}</td>
                    <td className="px-3 py-2 text-[#0F172A] max-w-[260px] truncate" title={r.subject}>{r.subject}</td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      {r.ok ? (
                        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#E8ECE7] text-[#166534] font-semibold">
                          <CheckCircle2 className="w-3 h-3" /> Accepted
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#FEE2E2] text-[#991B1B] font-semibold" title={r.error || ""}>
                          <ShieldAlert className="w-3 h-3" /> Failed
                        </span>
                      )}
                      {r.resend_id && (
                        <div className="text-[10px] font-mono text-[#7E937D] mt-1">{r.resend_id.slice(0, 8)}…</div>
                      )}
                      {!r.ok && r.error && (
                        <div className="text-[10px] text-[#991B1B] mt-1 max-w-[260px] break-words">{r.error}</div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="bg-white border border-[#E5E5E0] rounded-2xl p-6" data-testid="teams-card">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#DBEAFE] text-[#1E40AF] flex items-center justify-center">
              <Plug className="w-5 h-5" strokeWidth={1.75} />
            </div>
            <div>
              <h2 className="font-display text-lg font-semibold text-[#0F172A]">Microsoft Teams</h2>
              <p className="text-xs text-[#1E3A8A] mt-0.5">
                Posts to a Power Automate webhook that renders Adaptive Cards in your channel.
              </p>
              {data.configured ? (
                <span
                  data-testid="teams-configured-badge"
                  className="mt-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#E8ECE7] text-[#166534] font-semibold"
                >
                  <CheckCircle2 className="w-3 h-3" strokeWidth={2} /> Configured
                </span>
              ) : (
                <span
                  className="mt-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#FEF3C7] text-[#92400E] font-semibold"
                >
                  <ShieldAlert className="w-3 h-3" strokeWidth={2} /> Not configured
                </span>
              )}
            </div>
          </div>
          <button
            onClick={sendTest}
            disabled={testing || !data.configured}
            data-testid="teams-test-btn"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#1E3A8A] text-white text-sm font-medium disabled:opacity-60 hover:bg-[#172554]"
          >
            {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Send test ping
          </button>
        </div>

        <div className="border-t border-[#F0EFEB] pt-4">
          <div className="hw-eyebrow text-[10px] mb-3">Which events should ping Teams?</div>
          <ul className="space-y-2" data-testid="event-toggles">
            {data.catalog.map((evt) => {
              const enabled = data.events[evt.key] !== false;
              const locked = !!evt.locked;
              return (
                <li
                  key={evt.key}
                  data-testid={`event-${evt.key}`}
                  className={`flex items-start justify-between gap-3 p-3 rounded-xl border transition-colors ${
                    enabled || locked ? "bg-[#F9F8F6] border-[#E5E5E0]" : "bg-white border-[#E5E5E0] opacity-70"
                  }`}
                >
                  <div className="flex-1">
                    <div className="text-sm font-medium text-[#0F172A]">{evt.label}</div>
                    <div className="text-xs text-[#1E3A8A] mt-0.5">{evt.description}</div>
                  </div>
                  <label className="inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={enabled}
                      disabled={locked || saving}
                      onChange={() => toggle(evt.key, locked)}
                      data-testid={`toggle-${evt.key}`}
                      className="sr-only peer"
                    />
                    <div className={`relative w-10 h-5 rounded-full transition-colors ${
                      locked ? "bg-[#1E3A8A] opacity-60" : enabled ? "bg-[#1E3A8A]" : "bg-[#E5E5E0]"
                    }`}>
                      <div className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform ${
                        enabled || locked ? "translate-x-5" : "translate-x-0"
                      }`} />
                    </div>
                  </label>
                </li>
              );
            })}
          </ul>
        </div>
      </div>

      {/* Resource category audit & cleanup (admin tool) */}
      <ResourceCategoryCleanupCard />

      {/* Unite Us inbound referral webhook */}
      <UniteUsCard data={uniteUs} rows={uniteUsRows} loading={uniteUsLoading} onRefresh={loadUniteUs} />
    </div>
  );
}

function UniteUsCard({ data, rows, loading, onRefresh }) {
  if (!data) {
    return (
      <div className="bg-white border border-[#E5E5E0] rounded-2xl p-6" data-testid="unite-us-card-loading">
        <div className="text-xs text-[#1E3A8A]">Loading Unite Us status…</div>
      </div>
    );
  }
  if (data.unavailable) {
    // Logged-in user lacks ADMIN_ED — silently hide the card.
    return null;
  }

  const enabled = !!data.inbound_enabled;
  const hasSecret = !!data.has_secret;
  const fullUrl = (typeof window !== "undefined" ? window.location.origin : "") + (data.webhook_url_suffix || "");

  const copyUrl = () => {
    if (!fullUrl) return;
    navigator.clipboard?.writeText(fullUrl);
    toast.success("Webhook URL copied");
  };

  return (
    <div className="bg-white border border-[#E5E5E0] rounded-2xl p-6" data-testid="unite-us-card">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#DCFCE7] text-[#166534] flex items-center justify-center">
            <Webhook className="w-5 h-5" strokeWidth={1.75} />
          </div>
          <div>
            <h2 className="font-display text-lg font-semibold text-[#0F172A]">Unite Us inbound referrals</h2>
            <p className="text-xs text-[#1E3A8A] mt-0.5 max-w-xl">
              Partner agencies in the Unite Us network can POST referrals straight into our Smart Inbox.
              Referrals are HMAC-signed, deduplicated by external referral id, and surfaced on the intake queue.
            </p>
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              {enabled && hasSecret ? (
                <span data-testid="unite-us-status-on" className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#E8ECE7] text-[#166534] font-semibold">
                  <CheckCircle2 className="w-3 h-3" /> Inbound enabled
                </span>
              ) : (
                <span data-testid="unite-us-status-off" className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#FEF3C7] text-[#92400E] font-semibold">
                  <ShieldAlert className="w-3 h-3" /> {enabled ? "Secret missing" : "Inbound disabled"}
                </span>
              )}
              {data.environment && (
                <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#F1F5F9] text-[#1E3A8A] font-semibold capitalize">
                  Env: {data.environment}
                </span>
              )}
              {typeof data.received_count === "number" && (
                <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#EFF6FF] text-[#1E40AF] font-semibold">
                  {data.received_count} received total
                </span>
              )}
            </div>
          </div>
        </div>
        <button
          onClick={onRefresh}
          disabled={loading}
          data-testid="unite-us-refresh-btn"
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-white border border-[#E5E5E0] text-xs text-[#1E3A8A] hover:bg-[#F9F8F6]"
        >
          {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} Refresh
        </button>
      </div>

      <div className="border-t border-[#F0EFEB] pt-4 grid sm:grid-cols-2 gap-4 text-xs text-[#334155]">
        <div className="sm:col-span-2">
          <div className="hw-eyebrow text-[10px] mb-1">Webhook URL (share with Unite Us partner team)</div>
          <div className="flex items-center gap-2 flex-wrap">
            <code data-testid="unite-us-webhook-url" className="font-mono text-[12px] break-all bg-[#F9F8F6] border border-[#E5E5E0] rounded-lg px-2 py-1 flex-1 min-w-[260px]">{fullUrl || data.webhook_url_suffix}</code>
            <button onClick={copyUrl} data-testid="unite-us-copy-url" className="inline-flex items-center gap-1 text-[11px] px-3 py-1.5 rounded-full bg-white border border-[#E5E5E0] text-[#1E3A8A] hover:bg-[#F9F8F6]">
              <Copy className="w-3 h-3" /> Copy
            </button>
          </div>
        </div>
        <div>
          <div className="hw-eyebrow text-[10px] mb-1">Signature header</div>
          <div className="font-mono text-[12px] break-all">{data.signature_header || "—"}</div>
        </div>
        <div>
          <div className="hw-eyebrow text-[10px] mb-1">HMAC algorithm</div>
          <div className="font-mono text-[12px] uppercase">{data.algorithm || "—"}</div>
        </div>
        <div>
          <div className="hw-eyebrow text-[10px] mb-1">Webhook secret</div>
          <div className="font-mono text-[12px]">{hasSecret ? "•••••••• (configured)" : "(not configured)"}</div>
        </div>
        <div>
          <div className="hw-eyebrow text-[10px] mb-1">Last referral received</div>
          <div className="font-mono text-[12px]">{data.last_received_at ? new Date(data.last_received_at).toLocaleString() : "—"}</div>
        </div>
      </div>

      {!enabled && (
        <div className="mt-4 p-3 rounded-xl bg-[#FEF3C7] border border-[#FCD34D] text-[11px] text-[#92400E] inline-flex items-start gap-2">
          <AlertCircle className="w-3.5 h-3.5 flex-none mt-0.5" />
          <div>
            <div className="font-semibold">Inbound is currently disabled.</div>
            <div className="mt-0.5">
              Set <code className="font-mono bg-white/60 px-1 rounded">UNITE_US_INBOUND_ENABLED=true</code> and provide
              <code className="font-mono bg-white/60 px-1 rounded ml-1">UNITE_US_WEBHOOK_SECRET</code> in the production environment, then restart the backend.
            </div>
          </div>
        </div>
      )}

      <div className="border-t border-[#F0EFEB] pt-4 mt-4">
        <div className="flex items-center justify-between mb-2">
          <div className="hw-eyebrow text-[10px]">Recent inbound referrals (latest 10)</div>
          <a
            href="/inbox"
            data-testid="unite-us-open-inbox"
            className="inline-flex items-center gap-1 text-[11px] text-[#1E3A8A] underline decoration-dotted"
          >
            <ExternalLink className="w-3 h-3" /> Open Smart Inbox
          </a>
        </div>
        {loading ? (
          <div className="text-xs text-[#1E3A8A]">Loading…</div>
        ) : !rows || rows.length === 0 ? (
          <div className="text-xs text-[#7E937D]" data-testid="unite-us-empty">No Unite Us referrals received yet.</div>
        ) : (
          <div className="overflow-x-auto border border-[#E5E5E0] rounded-xl" data-testid="unite-us-recent-table">
            <table className="w-full text-xs">
              <thead className="bg-[#F9F8F6] text-[#1E3A8A] text-left">
                <tr>
                  <th className="px-3 py-2 font-semibold">When</th>
                  <th className="px-3 py-2 font-semibold">Referral id</th>
                  <th className="px-3 py-2 font-semibold">Care Seeker</th>
                  <th className="px-3 py-2 font-semibold">Needs</th>
                  <th className="px-3 py-2 font-semibold">Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-t border-[#F0EFEB] align-top">
                    <td className="px-3 py-2 whitespace-nowrap text-[#334155]">{r.created_at ? new Date(r.created_at).toLocaleString() : "—"}</td>
                    <td className="px-3 py-2 font-mono text-[11px] text-[#1E3A8A] break-all max-w-[160px]">{r.external_referral_id || "—"}</td>
                    <td className="px-3 py-2 text-[#0F172A] whitespace-nowrap">{[r.first_name, r.last_name].filter(Boolean).join(" ") || "—"}</td>
                    <td className="px-3 py-2 text-[#334155] max-w-[220px] truncate" title={Array.isArray(r.needs) ? r.needs.join(", ") : (r.needs || "")}>
                      {Array.isArray(r.needs) ? r.needs.join(", ") : (r.needs || "—")}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap capitalize text-[11px]">{r.status || "new"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
